import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  completeFeedback,
  feedbackQuery,
  plannedTicketQuery,
  rejectPlanning,
  reopenTicket,
  resolvedTicketQuery,
  sendFeedback,
  TicketLinkGone,
  TicketLinkRefused,
} from '../api/easeedeskTicketLinks'
import { Spinner } from '../ui/Spinner'

/**
 * Cele trei pagini pe care le deschide un link de email, de un om care nu are
 * cont in portal:
 *
 *   easeedesk/reject_planned_ticket/<jeton>   respinge o planificare
 *   easeedesk/reopen_resolved_ticket/<jeton>  redeschide un tichet rezolvat
 *   easeedesk/ticket_feedback/<jeton>/<nota>  parerea clientului
 *
 * ADRESELE RAMAN CELE VECHI, cuvant cu cuvant. Linkurile au plecat deja prin
 * email si nu se mai pot schimba; o adresa noua ar face ca tot ce s-a trimis
 * pana azi sa duca in gol.
 *
 * Ca si tichetul vazut de client, paginile astea se deseneaza PE LANGA
 * scheletul aplicatiei (vezi `Shell`): fara antet, fara meniuri, fara nimic
 * care sa ceara o sesiune. In portal era `staticTemplate`.
 *
 * Ce le apara e jetonul, verificat pe server. Aici nu se verifica nimic si nu
 * se ascunde nimic dupa un `if` de desen: cand serverul spune 410, se arata
 * pagina "linkul nu mai e bun"; cand spune 409, se arata chiar ce a spus.
 */

/* -------------------------------------------------------------------------
 * SCHELETUL COMUN
 * ---------------------------------------------------------------------- */

const PAGE: React.CSSProperties = {
  margin: 0,
  font: '16px/1.55 system-ui,-apple-system,Segoe UI,Roboto,sans-serif',
  background: '#f4f6f8',
  color: '#22303f',
  minHeight: '100vh',
}

const CARD: React.CSSProperties = {
  maxWidth: '34rem',
  margin: '12vh auto',
  padding: '2rem',
  background: '#fff',
  borderRadius: 10,
  boxShadow: '0 1px 3px rgba(0,0,0,.12)',
}

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div style={PAGE}>
      <div style={CARD}>{children}</div>
    </div>
  )
}

/**
 * Linkul nu deschide nimic - jeton gresit, vechi, sau tichet care nu mai e.
 *
 * Un singur mesaj pentru toate trei. Portalul le deosebea ("Invalid token",
 * "Ticket not found"), iar deosebirea aia spune unui strain daca a nimerit un
 * jeton adevarat, fara sa-i foloseasca cu nimic celui care a apasat linkul.
 */
/**
 * `used` - pentru aprobare: o a doua deschidere spune ca linkul a fost deja
 * folosit SAU a expirat, cu cuvintele portalului (`Ticket_approval`). Tot nu
 * spune care din doua, din acelasi motiv ca mai sus.
 */
function LinkGone({ used = false }: { used?: boolean }) {
  return (
    <Frame>
      <h1 style={{ margin: '0 0 .75rem', fontSize: '1.35rem' }}>This link is no longer valid</h1>
      <p style={{ margin: '0 0 .75rem' }}>
        {used ? 'It may have been used already, or it may have expired.' : 'The link you followed cannot be opened any more.'}
      </p>
      <p style={{ margin: 0 }}>Please reply to the ticket email, or ask your service desk to send the link again.</p>
    </Frame>
  )
}

/** Linkul e bun, dar ceva s-a schimbat intre timp - si asta se spune. */
function Refused({ message }: { message: string }) {
  return (
    <Frame>
      <div className="alert alert-warning" style={{ margin: 0 }}>
        {message}
      </div>
    </Frame>
  )
}

function Thanks({ message }: { message: string }) {
  return (
    <Frame>
      <div className="alert alert-success text-center" style={{ margin: 0 }}>
        {message}
      </div>
    </Frame>
  )
}

/** Ce s-a intors dintr-o cerere care n-a mers, pe intelesul paginii. */
function problem(error: unknown): { gone: boolean; message: string } {
  if (error instanceof TicketLinkGone) return { gone: true, message: error.message }
  if (error instanceof TicketLinkRefused) return { gone: false, message: error.message }

  return { gone: false, message: 'Something went wrong. Please try again.' }
}

/* -------------------------------------------------------------------------
 * FORMULARUL CU UN MOTIV - acelasi la amandoua paginile care il cer
 * ---------------------------------------------------------------------- */

function ReasonForm({
  title,
  hint,
  ticket,
  onSave,
  done,
}: {
  title: string
  hint?: string
  ticket: number
  onSave: (comments: string) => Promise<unknown>
  done: string
}) {
  const [comments, setComments] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [gone, setGone] = useState(false)

  if (gone) return <LinkGone />
  if (saved) return <Thanks message={done} />

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (comments.trim() === '' || saving) return

    setSaving(true)
    setError(null)
    try {
      await onSave(comments)
      setSaved(true)
    } catch (failed) {
      const what = problem(failed)
      if (what.gone) setGone(true)
      else setError(what.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Frame>
      <h1 style={{ margin: '0 0 .75rem', fontSize: '1.35rem' }}>
        {title} #{ticket}
      </h1>
      {hint ? <p style={{ margin: '0 0 1rem' }}>{hint}</p> : null}

      {error ? (
        <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      ) : null}

      <form onSubmit={submit}>
        <label htmlFor="comments" style={{ display: 'block', marginBottom: '.35rem' }}>
          Comments <span className="required">*</span>
        </label>
        <textarea
          id="comments"
          className="form-control"
          rows={5}
          required
          value={comments}
          onChange={(event) => setComments(event.target.value)}
        />
        <div style={{ textAlign: 'right', marginTop: '1rem' }}>
          <button className="btn btn-primary" type="submit" disabled={saving || comments.trim() === ''}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Frame>
  )
}

/* -------------------------------------------------------------------------
 * PLANIFICARE RESPINSA
 * ---------------------------------------------------------------------- */

export function RejectPlanningPage({ token }: { token: string }) {
  const q = useQuery(plannedTicketQuery(token))

  if (q.isError) {
    const what = problem(q.error)

    return what.gone ? <LinkGone /> : <Refused message={what.message} />
  }
  if (!q.data) return <Spinner />

  return (
    <ReasonForm
      title="Reject the planning for ticket"
      hint="Please tell us why the planned date does not work for you."
      ticket={q.data.ticket}
      onSave={(comments) => rejectPlanning(token, comments)}
      done="Thank you. The planning has been rejected."
    />
  )
}

/* -------------------------------------------------------------------------
 * REDESCHIDEREA UNUI TICHET REZOLVAT
 * ---------------------------------------------------------------------- */

export function ReopenResolvedPage({ token }: { token: string }) {
  const q = useQuery(resolvedTicketQuery(token))

  if (q.isError) {
    const what = problem(q.error)

    return what.gone ? <LinkGone /> : <Refused message={what.message} />
  }
  if (!q.data) return <Spinner />

  return (
    <ReasonForm
      title="Reopen ticket"
      hint="Please enter below the reason why you think that this ticket has not been resolved."
      ticket={q.data.ticket}
      onSave={(comments) => reopenTicket(token, comments)}
      done="Thank you. The ticket has been reopened."
    />
  )
}

/* -------------------------------------------------------------------------
 * APROBARE SI RESPINGERE - pagina are fisierul ei (`TicketApprovalPage.tsx`),
 * cu foaia ei; ramane exportata si de aici, unde o cauta `router.tsx`.
 * ---------------------------------------------------------------------- */

export { TicketApprovalPage } from './TicketApprovalPage'

/* -------------------------------------------------------------------------
 * PAREREA CLIENTULUI
 * ---------------------------------------------------------------------- */

/** Cuvintele de sub stele, cu aceleasi praguri ca macheta veche. */
function ratingText(stars: number): string {
  if (stars >= 0.5 && stars <= 1.5) return 'Bad Experience'
  if (stars >= 2 && stars <= 3) return 'Not Satisfied'
  if (stars >= 3.5 && stars < 4) return 'As Expected'
  if (stars === 4) return 'Very good'
  if (stars > 4) return 'Fantastic'

  return ''
}

export function TicketFeedbackPage({ token, rating }: { token: string; rating: number }) {
  const q = useQuery(feedbackQuery(token, rating))

  const [comments, setComments] = useState('')
  const [picked, setPicked] = useState<Record<number, number>>({})
  const [stars, setStars] = useState(rating / 2)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  /*
   * Nota se scrie la DESCHIDEREA paginii, nu la apasarea butonului.
   *
   * Asa face si portalul: macheta trimite `sendTicketFeedback` din `$(function
   * () {...})`, adica in clipa in care pagina s-a desenat. Clientul care a
   * apasat steaua din email si-a spus deja parerea acolo; restul paginii -
   * comentariul si insusirile - e in plus, si de multe ori nu se completeaza.
   *
   * `useRef` fiindca in React lucrat pe doua treceri (StrictMode) efectul
   * porneste de doua ori, si a doua nota ar fi un al doilea rand in
   * `it_easeedesk_ticket_feedback`, cu aceeasi valoare.
   */
  const sent = useRef(false)
  useEffect(() => {
    if (sent.current || !q.data) return
    sent.current = true
    void sendFeedback(token, rating).catch(() => {
      /* Nota nu s-a putut scrie - pagina ramane deschisa, si al doilea pas o
         spune limpede daca nici el nu gaseste randul. */
    })
  }, [q.data, token, rating])

  if (q.isError) {
    const what = problem(q.error)

    return what.gone ? <LinkGone /> : <Refused message={what.message} />
  }
  if (!q.data) return <Spinner />
  if (saved) return <Thanks message={saved} />

  const page = q.data

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (saving) return

    setSaving(true)
    setError(null)
    try {
      await completeFeedback(token, rating, {
        comments,
        score_rating: stars,
        attribute_id: picked,
      })
      setSaved('Thank you! Your feedback has been sent.')
    } catch (failed) {
      setError(problem(failed).message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Frame>
      <div className="text-center" style={{ marginBottom: '1.25rem' }}>
        {page.assignee.picture ? (
          <img className="imgiconavatar" alt="" src={page.assignee.picture} style={{ maxWidth: 96, borderRadius: '50%' }} />
        ) : null}
        <div className="name-user-feedback" style={{ fontWeight: 600, marginTop: '.5rem' }}>
          {page.assignee.name}
        </div>
      </div>

      <form onSubmit={submit}>
        <div className="text-center" style={{ marginBottom: '1.25rem' }}>
          <div className="rating_stars" role="radiogroup" aria-label="Rating">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                role="radio"
                aria-checked={stars === star}
                aria-label={`${star} stars`}
                onClick={() => setStars(star)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.75rem',
                  cursor: 'pointer',
                  color: star <= stars ? '#f0ad4e' : '#ccd4dc',
                }}
              >
                ★
              </button>
            ))}
          </div>
          <div style={{ marginTop: '.35rem' }}>{ratingText(stars)}</div>
        </div>

        {page.attributes.length > 0 ? (
          <div className="wrapcompliments" style={{ marginBottom: '1.25rem' }}>
            <div className="title-compliments-feedback text-center" style={{ marginBottom: '.5rem' }}>
              Give a compliment
            </div>
            <ul className="compliments_list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {page.attributes.map((attribute) => (
                <li
                  key={attribute.id}
                  className="thumbs-compliments-qs"
                  style={{ display: 'flex', alignItems: 'center', gap: '.5rem', padding: '.25rem 0' }}
                >
                  <span className="title_compliments" style={{ flex: 1 }}>
                    {attribute.name}
                  </span>
                  <button
                    type="button"
                    aria-pressed={picked[attribute.id] === 0}
                    aria-label={`Dislike ${attribute.name}`}
                    className={picked[attribute.id] === 0 ? 'thumbs-compliments thumbs-down active' : 'thumbs-compliments thumbs-down'}
                    onClick={() => setPicked({ ...picked, [attribute.id]: 0 })}
                  >
                    <i className="far fa-thumbs-down" aria-hidden="true"></i>
                  </button>
                  <button
                    type="button"
                    aria-pressed={picked[attribute.id] === 1}
                    aria-label={`Like ${attribute.name}`}
                    className={picked[attribute.id] === 1 ? 'thumbs-compliments thumbs-up active' : 'thumbs-compliments thumbs-up'}
                    onClick={() => setPicked({ ...picked, [attribute.id]: 1 })}
                  >
                    <i className="far fa-thumbs-up" aria-hidden="true"></i>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="wrapfeedbackmsg" style={{ marginBottom: '1rem' }}>
          <label htmlFor="feedback-comments" className="titlemsj" style={{ display: 'block', marginBottom: '.35rem' }}>
            Write a note about this service
          </label>
          <textarea
            id="feedback-comments"
            className="form-control note-textarea"
            rows={4}
            value={comments}
            onChange={(event) => setComments(event.target.value)}
          />
        </div>

        {error ? (
          <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>
            {error}
          </div>
        ) : null}

        <div className="text-center">
          <button className="btn btn-primary btn-submit-feedback" type="submit" disabled={saving}>
            {saving ? 'Sending…' : 'SUBMIT'}
          </button>
          <div>
            <button
              type="button"
              className="btn btn-link btn-skip-feedback"
              onClick={() => setSaved('Thank you for your feedback!')}
            >
              Skip
            </button>
          </div>
        </div>
      </form>
    </Frame>
  )
}
